import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { LockIcon, MessageSquareQuoteIcon, UsersIcon } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import {
  ReviewQuestionType,
  type UpwardFeedbackResult,
  type UpwardQuestionResult,
  type UpwardRatingResult,
} from '@gokrew/models/performance';
import { cn } from '@/lib/utils';
import { formatPercent } from '@/utility/percent';
import { formatScore } from '@/utility/localizedNumbers';
import styles from './TeamFeedbackResult.module.css';

export interface TeamFeedbackResultProps {
  result: UpwardFeedbackResult;
  className?: string;
}

const percent = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 100) : 0;

/** A rating: its average and how the answers spread over the levels. */
const RatingRow = ({ question }: { question: UpwardRatingResult }) => {
  const { t } = useTranslation(TranslationNS.Performance);
  const total = question.distribution.reduce((a, b) => a + b, 0);
  return (
    <div className={styles.question}>
      <div className={styles.questionHeader}>
        <span className={styles.questionTitle}>{question.title}</span>
        <span className={styles.average}>
          {question.average == null
            ? t('shared.teamFeedback.noAnswer')
            : t('shared.teamFeedback.average', {
                average: formatScore(question.average),
                max: question.scaleMax,
              })}
        </span>
      </div>
      <div
        className={styles.bar}
        role="img"
        aria-label={question.distribution
          .map((count, i) => `${question.scaleLabels?.[i] ?? i + 1}: ${count}`)
          .join(', ')}
      >
        {question.distribution.map((count, i) =>
          count ? (
            <span
              key={i}
              className={cn(styles.segment, styles[`level${i + 1}`])}
              style={{ flexGrow: count }}
              title={`${question.scaleLabels?.[i] ?? i + 1}: ${formatPercent(percent(count, total))}`}
            />
          ) : null
        )}
      </div>
      {question.comments.length > 0 && (
        <Comments comments={question.comments} />
      )}
    </div>
  );
};

/**
 * What the colours of the bars mean: one swatch per level of the first
 * rating's scale, with the level's name when the scale has names.
 */
const ScaleLegend = ({ questions }: { questions: UpwardQuestionResult[] }) => {
  const { t } = useTranslation(TranslationNS.Performance);
  const rating = questions.find(
    (q): q is UpwardRatingResult => q.type === ReviewQuestionType.RATING
  );
  if (!rating) return null;
  return (
    <ul className={styles.legend} aria-label={t('shared.teamFeedback.legend')}>
      {Array.from({ length: rating.scaleMax }, (_, i) => (
        <li key={i} className={styles.legendItem}>
          <span
            className={cn(styles.legendSwatch, styles[`level${i + 1}`])}
            aria-hidden
          />
          {rating.scaleLabels?.[i] ?? i + 1}
        </li>
      ))}
    </ul>
  );
};

const Comments = ({ comments }: { comments: string[] }) => (
  <ul className={styles.comments}>
    {comments.map((comment, i) => (
      // The order is random and says nothing: the index is the only key.
      <li key={i} className={styles.comment}>
        <MessageSquareQuoteIcon className={styles.commentIcon} aria-hidden />
        <span>{comment}</span>
      </li>
    ))}
  </ul>
);

const QuestionRow = ({ question }: { question: UpwardQuestionResult }) => {
  const { t } = useTranslation(TranslationNS.Performance);
  if (question.type === ReviewQuestionType.RATING) {
    return <RatingRow question={question} />;
  }
  if (question.type === ReviewQuestionType.MULTIPLE_CHOICE) {
    return (
      <div className={styles.question}>
        <span className={styles.questionTitle}>{question.title}</span>
        <ul className={styles.options}>
          {question.options.map((option) => (
            <li key={option.optionId} className={styles.option}>
              <span>{option.label}</span>
              <span className={styles.optionCount}>
                {formatPercent(percent(option.count, question.responseCount))}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className={styles.question}>
      <span className={styles.questionTitle}>{question.title}</span>
      {question.comments.length ? (
        <Comments comments={question.comments} />
      ) : (
        <p className={styles.muted}>{t('shared.teamFeedback.noComment')}</p>
      )}
    </div>
  );
};

/**
 * A manager's team feedback, grouped (spec 2026-10-29 upward feedback, U3):
 * how many answered out of how many asked, then, from the anonymity
 * threshold only, the score, each question's average and spread, and the
 * comments in random order. Never who answered: the data has no names.
 */
export const TeamFeedbackResult = memo(
  ({ result, className }: TeamFeedbackResultProps) => {
    const { t } = useTranslation(TranslationNS.Performance);
    return (
      <div className={cn(styles.result, className)}>
        <div className={styles.summary}>
          <div className={styles.stat}>
            <UsersIcon className={styles.statIcon} aria-hidden />
            <span>
              {t('shared.teamFeedback.responses', {
                count: result.responses,
                invited: result.invited,
              })}
              {result.responseRate != null &&
                ` (${formatPercent(result.responseRate)})`}
            </span>
          </div>
          {!result.belowThreshold && result.score != null && (
            <div className={styles.score}>
              {t('shared.teamFeedback.score', {
                score: formatScore(result.score),
              })}
            </div>
          )}
        </div>
        <p className={styles.anonymous}>
          <LockIcon className={styles.anonymousIcon} aria-hidden />
          {t('shared.teamFeedback.anonymous', { count: result.threshold })}
        </p>
        {result.belowThreshold ? (
          <p className={styles.notEnough} role="status">
            {t('shared.teamFeedback.notEnough', { count: result.threshold })}
          </p>
        ) : (
          <div className={styles.questions}>
            <ScaleLegend questions={result.questions} />
            {result.questions.map((question) => (
              <QuestionRow key={question.questionId} question={question} />
            ))}
          </div>
        )}
      </div>
    );
  }
);

TeamFeedbackResult.displayName = 'TeamFeedbackResult';
